<template>
  <Listbox
    as="div"
    class="relative grow"
    :modelValue="modelValue"
    :disabled="props.disabled"
    :multiple="props.multiple"
    @update:modelValue="handleUsersChange"
  >
    <ListboxButton
      ref="usersButton"
      class="relative flex w-full cursor-pointer rounded-sm border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 sm:text-sm"
      :class="{
        '!cursor-not-allowed': disabled,
      }"
    >
      <span class="grow flex items-center">
        <span
          class="flex flex-wrap items-center gap-2"
          v-if="assignees.length >= 2"
        >
          <user-avatar
            v-for="assignee in assignees"
            :user="assignee"
            :key="assignee.id"
          />
        </span>
        <span v-if="assignees.length === 1" class="flex items-center space-x-2">
          <user-avatar :user="assignees[0]" :size="5" />
          <span>{{ assignees[0].fullname }}</span>
        </span>
        <span
          v-if="assignees.length === 0"
          class="flex flex-nowrap items-center text-gray-500"
        >
          <span>{{
            emptyLabel ?? t("components.inputs.expansion.no_value")
          }}</span>
        </span>
      </span>
      <span
        class="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400"
      >
        <ChevronDownIcon
          v-if="assignees.length === 0 || !allowClearing"
          class="h-5 w-5"
          aria-hidden="true"
        />
        <XMarkIcon
          v-else
          @click.prevent="clear"
          class="h-5 w-5"
          aria-hidden="true"
        />
      </span>
    </ListboxButton>

    <transition
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <ListboxOptions
        :class="{
          'bottom-full mt-0 mb-3': shouldRenderOnTop,
        }"
        class="z-20 mt-3 max-h-56 max-w-md overflow-auto rounded-sm bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-sm absolute left-0 right-0 sm:left-auto sm:right-auto"
      >
        <FilterSearchInput
          v-model="searchQuery"
          :placeholder="t('components.inputs.searchUsers')"
        />
        <template v-if="filteredUsers.length > 0">
          <li
            v-if="allowUnassigned"
            :class="[
              unassigned ? 'text-gray-900 bg-gray-100' : 'text-gray-900',
              'relative cursor-pointer select-none py-2 pl-3 pr-9',
            ]"
            @click="handleUsersChange(props.multiple ? [] : null)"
          >
            <div class="flex items-center space-x-2">
              <input
                class="h-4 w-4 rounded-xs border-gray-300 text-brand-900 focus:outline-0 focus:ring-offset-0 focus:ring-0"
                type="checkbox"
                :checked="unassigned"
              />
              <user-avatar />
              <span
                :class="['truncate font-normal', unassigned && 'font-semibold']"
              >
                {{ t("components.inputs.unassigned") }}
              </span>
            </div>
          </li>
          <ListboxOption
            as="template"
            v-for="user in filteredUsers"
            :key="user.id"
            :value="user.id"
            v-slot="{ active, selected }"
          >
            <li
              :class="[
                active ? 'text-gray-900 bg-gray-100' : 'text-gray-900',
                'relative cursor-pointer select-none py-2 pl-3 pr-9',
              ]"
            >
              <div class="flex items-center space-x-2">
                <input
                  class="h-4 w-4 rounded-xs border-gray-300 text-brand-900 focus:ring-0 focus:outline-0 focus:ring-offset-0"
                  type="checkbox"
                  :checked="selected"
                />
                <user-avatar :user="user" />
                <span
                  :class="['truncate font-normal', selected && 'font-semibold']"
                >
                  {{ user.fullname }}
                </span>
              </div>
            </li>
          </ListboxOption>
        </template>

        <ListboxOption
          disabled
          v-else
          class="px-3 py-2 text-gray-500 whitespace-nowrap"
        >
          {{ t("components.no_result") }}
        </ListboxOption>
      </ListboxOptions>
    </transition>
  </Listbox>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref } from "vue";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/vue";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/vue/20/solid";
import { useI18n } from "vue-i18n";

import { useUsers } from "@/Tailwind/Mixins/useUsers";
import UserAvatar from "@/Tailwind/Components/UserAvatar";
import FilterSearchInput from "@/Tailwind/Components/Input/Filters/FilterSearchInput";
import { useReactiveRendering } from "@/Tailwind/Mixins/useReactiveRendering";

const props = defineProps({
  tenantId: String,
  modelValue: Array,
  disabled: Boolean,
  horizontalLabelWidth: {
    type: String,
    default: "0rem",
  },
  emptyLabel: String,
  multiple: {
    type: Boolean,
    default: true,
  },
  allowUnassigned: {
    type: Boolean,
    default: true,
  },
  allowClearing: {
    type: Boolean,
    default: true,
  },
});

const { t } = useI18n();

const emit = defineEmits(["change", "clear"]);

const users = computed(() => useUsers(props.tenantId));
const assignees = computed(() =>
  users.value.filter((user) => props.modelValue.includes(user.id))
);

const unassigned = computed(
  () => !assignees.value || assignees.value.length === 0
);

// Search
const searchQuery = ref("");

const filteredUsers = computed(() => {
  if (!searchQuery.value) {
    return users.value;
  }

  return users.value.filter((user) =>
    user.fullname.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

function handleUsersChange(users) {
  emit("change", users);
}

function clear() {
  emit("change", props.multiple ? [] : null);
}

const usersButton = ref(null);
const minimumRequiredSpace = computed(() => {
  // Each list item element is 36px tall
  // The container is max 224px tall
  // Search input is 50px tall
  // Leave 70px at the bottom as a margin (Some screens have floating elements)
  return Math.min(filteredUsers.value.length * 36 + 50, 224) + 70;
});

const { shouldRenderOnTop } = useReactiveRendering(
  usersButton,
  minimumRequiredSpace,
  props.scrollContainer
);
</script>
